import {
  CustomerUploadContentType,
  type CartLineCustomization,
  type Customization,
  type SizeSet,
} from '@nucleus-commerce/api-client/admin';

/**
 * Raw form values of a line's customizations, keyed by customization key. A file customization
 * holds the media id staff picked, or the id of the file already on the line.
 */
export type CustomizationValues = Record<string, string | boolean>;

/** The file already on a line, per customization key: sent back as is, it keeps that file. */
export type KeptFiles = Record<string, string>;

export type CustomizationInput =
  { key: string; value: string | number | boolean } | { key: string; value: { mediaId: string } };

const PLATFORM_UPLOAD_TYPES: readonly string[] = Object.values(CustomerUploadContentType);

/** Content types a file customization takes: its own list, else every type the platform accepts. */
export function acceptedTypes(def: Pick<Customization, 'accept'>): readonly string[] {
  return def.accept && def.accept.length > 0 ? def.accept : PLATFORM_UPLOAD_TYPES;
}

/**
 * A select draws its choices from a size set or from inline options, never both. The size set's
 * values are copied onto `options` so the form, its schema and the labels read one field.
 */
export function withSizeSetOptions(
  def: Customization,
  sizeSets: readonly SizeSet[],
): Customization {
  if (!def.sizeSetId) return def;
  const set = sizeSets.find((s) => s.id === def.sizeSetId);
  return set
    ? { ...def, options: set.values.map((v) => ({ value: v.value, label: v.label })) }
    : def;
}

/** The product's customizations in dictionary order; ids the dictionary lacks are skipped. */
export function customizationsFor(
  ids: readonly string[],
  dictionary: readonly Customization[],
  sizeSets: readonly SizeSet[] = [],
): Customization[] {
  return dictionary
    .filter((c) => ids.includes(c.id))
    .sort((a, b) => a.position - b.position)
    .map((c) => withSizeSetOptions(c, sizeSets));
}

export function emptyCustomizationValues(defs: readonly Customization[]): CustomizationValues {
  return Object.fromEntries(defs.map((d) => [d.key, d.inputType === 'boolean' ? false : '']));
}

/** A line's saved answers as form values; a file reads as the id of the file on the line. */
export function customizationValuesOf(
  defs: readonly Customization[],
  saved: readonly CartLineCustomization[],
): CustomizationValues {
  return Object.fromEntries(
    defs.map((def): [string, string | boolean] => {
      const answer = saved.find((c) => c.key === def.key);
      if (def.inputType === 'boolean') return [def.key, answer?.value === true];
      if (!answer) return [def.key, ''];
      if (def.inputType === 'file') return [def.key, answer.file?.id ?? String(answer.value)];
      return [def.key, String(answer.value)];
    }),
  );
}

export function keptFilesOf(saved: readonly CartLineCustomization[]): KeptFiles {
  return Object.fromEntries(saved.flatMap((c) => (c.file ? [[c.key, c.file.id]] : [])));
}

const isBlank = (value: string | boolean | undefined) =>
  value === undefined || value === false || (typeof value === 'string' && value.trim() === '');

/**
 * The checks the backend runs before it answers 422 on `lines.N.customizations.M.value`, so the
 * person sees them before saving. Values are i18n keys by customization key. A file's type is
 * checked by the picker, which only offers files the customization accepts.
 */
export function validateCustomizations(
  defs: readonly Customization[],
  values: CustomizationValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const def of defs) {
    const value = values[def.key];
    if (isBlank(value)) {
      if (def.isRequired) {
        errors[def.key] =
          def.inputType === 'file'
            ? 'draftOrders:picker.validation.fileRequired'
            : 'draftOrders:picker.validation.customizationRequired';
      }
      continue;
    }
    const text = String(value).trim();
    if (def.inputType === 'select' && !def.options?.some((o) => o.value === text)) {
      errors[def.key] = 'draftOrders:picker.validation.customizationOption';
    } else if (def.inputType === 'number' && !Number.isFinite(Number(text))) {
      errors[def.key] = 'draftOrders:picker.validation.customizationNumber';
    } else if (def.inputType === 'text' && def.maxLength !== null && text.length > def.maxLength) {
      errors[def.key] = 'draftOrders:picker.validation.customizationTooLong';
    } else if (def.inputType === 'text' && def.pattern && !new RegExp(def.pattern).test(text)) {
      errors[def.key] = 'draftOrders:picker.validation.customizationPattern';
    }
  }
  return errors;
}

/**
 * Blank optional fields are left out; numbers and booleans keep their type. A file goes as
 * `{ mediaId }` when staff picked one, or as the line's own file id to keep it.
 */
export function toCustomizationInputs(
  defs: readonly Customization[],
  values: CustomizationValues,
  kept: KeptFiles = {},
): CustomizationInput[] {
  return defs.flatMap((def): CustomizationInput[] => {
    const value = values[def.key];
    if (isBlank(value)) return [];
    if (def.inputType === 'boolean') return [{ key: def.key, value: true }];
    const text = String(value).trim();
    if (def.inputType === 'file') {
      return kept[def.key] === text
        ? [{ key: def.key, value: text }]
        : [{ key: def.key, value: { mediaId: text } }];
    }
    return [{ key: def.key, value: def.inputType === 'number' ? Number(text) : text }];
  });
}
